import { I18nService } from '../../i18n/I18nService';
import { I18N_KEYS } from '../../i18n/keys';
import { getSocialLinks, hasSocialLinks } from '../../constants/socialConfig';

export class StatusBar {
  private i18n: I18nService;
  private element: HTMLElement;
  private calledEl: HTMLElement | null = null;
  private timeEl: HTMLElement | null = null;

  /** Called when user clicks the Terms of Service link */
  onTermsClick: (() => void) | null = null;

  constructor(i18n: I18nService) {
    this.i18n = i18n;
    this.element = document.getElementById('status-bar') || document.createElement('footer');
  }

  render(called: number, total: number, time: string): void {
    const socialLinks = getSocialLinks();
    const showSocial = hasSocialLinks();

    const socialLinksHtml = showSocial
      ? `
        <div class="status__social">
          ${socialLinks
            .map(
              ({ platform, url }) => `
            <a href="${url}" 
               target="_blank" 
               rel="noopener noreferrer" 
               class="status__social-link status__social-link--${platform}"
               aria-label="${this.getSocialLabel(platform)}">
              ${this.getSocialIcon(platform)}
            </a>
          `
            )
            .join('')}
        </div>
      `
      : '';

    const termsLinkText = this.i18n.t(I18N_KEYS.TERMS_LINK);

    this.element.innerHTML = `
      <div class="status__container">
        <div class="status__main-row">
          <div class="status__left">
            <span id="status-called" class="status__info">
              ${this.i18n.t(I18N_KEYS.STATUS_CALLED, { called, total })}
            </span>
            <span id="status-time" class="status__info">
              ${this.i18n.t(I18N_KEYS.STATUS_TIME, { time })}
            </span>
          </div>
        </div>
        <div class="status__footer-row">
          ${showSocial ? socialLinksHtml : ''}
          <button type="button" class="status__terms-link" id="status-terms-link">
            ${termsLinkText}
          </button>
        </div>
      </div>
    `;

    this.calledEl = this.element.querySelector('#status-called');
    this.timeEl = this.element.querySelector('#status-time');

    const termsLink = this.element.querySelector('#status-terms-link');
    if (termsLink) {
      termsLink.addEventListener('click', (e) => {
        e.preventDefault();
        this.onTermsClick?.();
      });
    }
  }

  update(called: number, total: number, time: string): void {
    if (this.calledEl) {
      this.calledEl.textContent = this.i18n.t(I18N_KEYS.STATUS_CALLED, { called, total });
    }
    if (this.timeEl) {
      this.timeEl.textContent = this.i18n.t(I18N_KEYS.STATUS_TIME, { time });
    }
  }

  updateLabels(called: number, total: number, time: string): void {
    this.update(called, total, time);
    const termsLink = this.element.querySelector('#status-terms-link');
    if (termsLink) {
      termsLink.textContent = this.i18n.t(I18N_KEYS.TERMS_LINK);
    }
  }

  private getSocialIcon(platform: 'facebook' | 'youtube' | 'zalo'): string {
    const icons: Record<string, string> = {
      facebook: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>`,
      youtube: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
      </svg>`,
      zalo: `<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 2C6.48 2 2 5.58 2 10c0 2.5 1.5 4.75 3.85 6.15L4.5 20l4.15-1.35C10.5 19.5 11.25 19.65 12 19.65c5.52 0 10-3.58 10-8s-4.48-8-10-8zm0 14c-.75 0-1.5-.15-2.15-.4l-.6-.2-2.4.8.8-2.35-.2-.6c-.25-.65-.4-1.4-.4-2.15 0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 6-6 6z"/>
        <circle cx="9" cy="10" r="1"/>
        <circle cx="12" cy="10" r="1"/>
        <circle cx="15" cy="10" r="1"/>
      </svg>`,
    };
    return icons[platform] || '<svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>';
  }

  private getSocialLabel(platform: 'facebook' | 'youtube' | 'zalo'): string {
    const labels: Record<string, string> = {
      facebook: 'Facebook',
      youtube: 'YouTube',
      zalo: 'Zalo',
    };
    return labels[platform] || 'Social Media';
  }
}
